import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Gauge } from "@digital-twins/design-system/icons";
import { SocialWalletUsageSummary } from "@digital-twins/shared-types";
import { Text } from "@/components/ui/Text";
import { socialMonthLine } from "./socialCredits";

interface SocialUsageMeterProps {
  usage: SocialWalletUsageSummary | null;
  className?: string;
}

/** The sentences of the meter line, in order. */
export function socialUsageMeterParts(
  usage: SocialWalletUsageSummary,
  t: TFunction<"app">,
  uiLanguage: string,
): Array<string> {
  return [socialMonthLine(t, uiLanguage, usage.creditsSpent, usage.postsKept)];
}

/**
 * The org's social wallet this month: "This month: 12.4 credits, 63 posts
 * added." Not mounted for
 * customers (the Library passes `showHeader={false}`, design S10); kept for
 * surfaces that still pass a summary. Renders nothing until it loads.
 */
export function SocialUsageMeter(props: SocialUsageMeterProps) {
  const { usage, className } = props;

  const { t, i18n } = useTranslation("app");

  if (!usage) {
    return null;
  }

  const parts = socialUsageMeterParts(usage, t, i18n.language);

  return (
    <Text
      as="p"
      size="sm"
      color="muted"
      className={className}
      role="status"
      aria-label={t("socialListening.usageMeter.aria")}
      data-testid="social-usage-meter"
    >
      <Gauge
        className="mr-1.5 inline h-3.5 w-3.5 align-[-2px]"
        aria-hidden="true"
      />
      {parts.join(" ")}
    </Text>
  );
}
